import { Mobile } from "@govlaunch/core";
import * as palette from "@govlaunch/palette";
import Popover from "@govlaunch/popover";
import ProfilePicture from "@govlaunch/profile-picture";
import { IGetGovernmentQuery } from "../../lib/queries/__generated__/GetGovernmentQuery.generated";
import DelayedHover from "../DelayedHover";
import Container from "../layout/Container";
import { getGovlaunchUrl } from "../utils/getGovlaunchUrl";

interface ITeamProps {
  government: NonNullable<IGetGovernmentQuery["government"]>;
}

export default function Team({ government }: ITeamProps) {
  const { members } = government;

  return (
    <Mobile>
      {(isMobile: boolean) => (
        <Container>
          <div
            css={{
              backgroundColor: palette.white,
              borderRadius: 5,
              border: `solid 1px ${palette.lightestGray}`
            }}
          >
            <div
              css={{
                padding: 15,
                borderBottom: `solid 1px ${palette.lightestGray}`,
                display: "flex",
                alignItems: "center"
              }}
            >
              <span
                css={{
                  textTransform: "uppercase",
                  fontSize: 12,
                  color: palette.sealBlue,
                  letterSpacing: 0.5,
                  fontWeight: 500
                }}
              >
                Team ({members.length})
              </span>
            </div>

            {isMobile ? (
              <div
                css={{
                  display: "grid",
                  gridTemplateColumns: "1fr",
                  padding: 15,
                  gridRowGap: 15
                }}
              >
                {members.map((member) => (
                  <div
                    key={member._id}
                    css={{
                      display: "flex",
                      alignItems: "center"
                    }}
                  >
                    <ProfilePicture
                      size={48}
                      image={member.avatar}
                      name={member.fullName}
                      innovator={member.isInnovator}
                      css={{
                        boxShadow: "0 4px 10px 0 #00000033"
                      }}
                    />

                    <div
                      css={{
                        marginLeft: 15,
                        display: "flex",
                        flexDirection: "column"
                      }}
                    >
                      <span
                        css={{
                          fontSize: 16,
                          fontWeight: 600
                        }}
                      >
                        {member.fullName}
                      </span>

                      <span
                        css={{
                          fontSize: 14,
                          color: palette.mediumGray
                        }}
                      >
                        {member.jobTitle}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div
                css={{
                  display: "flex",
                  padding: 15,
                  paddingRight: 0,
                  flexWrap: "wrap"
                }}
              >
                {members.map((member) => (
                  <div
                    key={member._id}
                    css={{
                      marginRight: 15,
                      display: "flex",
                      alignItems: "center"
                    }}
                  >
                    <a href={`${getGovlaunchUrl()}/@${member.slug}`} target="_blank" rel="noopener noreferrer">
                      <DelayedHover ms={300}>
                        {({ hover, ...bind }) => (
                          <Popover
                            placement="top"
                            isOpen={hover}
                            backgroundColor={palette.innovatorBlue}
                            borderColor={palette.innovatorBlue}
                            boxStyle={{
                              boxShadow: "0 10px 40px 0 rgba(34, 35, 40, 0.2)"
                            }}
                            render={() => (
                              <div
                                css={{
                                  padding: 10,
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                  color: palette.white
                                }}
                              >
                                <span
                                  css={{
                                    fontSize: 14,
                                    fontWeight: 600
                                  }}
                                >
                                  {member.fullName}
                                </span>

                                <span
                                  css={{
                                    fontSize: 12,
                                    fontWeight: 400
                                  }}
                                >
                                  {member.jobTitle}
                                </span>
                              </div>
                            )}
                          >
                            {({ innerRef }: { innerRef: any }) => (
                              <ProfilePicture
                                {...bind}
                                innerRef={innerRef}
                                size={60}
                                image={member.avatar}
                                name={member.fullName}
                                css={{
                                  boxShadow: "0 4px 10px 0 #00000033"
                                }}
                              />
                            )}
                          </Popover>
                        )}
                      </DelayedHover>
                    </a>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Container>
      )}
    </Mobile>
  );
}
